import type { UseQueryReturnType } from '@tanstack/vue-query'
import type { MaybeRef } from 'vue'
import type { ChatbotChatRequest, ChatbotSessionHistoryResponse, ChatbotSessionsResponse } from '@/types/chatbot.model'
import type { QueryOptions } from '@/types/query-options.model'
import { useQuery } from '@tanstack/vue-query'
import { unref } from 'vue'
import { useUserStore } from '@/stores/user.store'
import { HEADER_API_VERSION, HEADER_AUTHORIZATION, HEADER_CONTENT_TYPE, HEADER_ORG_ID, HEADER_TIMEZONE } from '@/utils/constants/headers'
import { getRequest, resolveTimezoneHeader } from './core/api-client'

const CACHE_KEY_CHATBOT = 'chatbot'
const CACHE_KEY_CHATBOT_HISTORY = 'chatbot-history'
const CHATBOT_DONE_SENTINEL = '[DONE]'
const CHATBOT_SSE_EVENT_CONTENT = 'content'
const CHATBOT_SSE_EVENT_STATUS = 'status'
const CARRIAGE_RETURN_SUFFIX_REGEX = /\r$/

interface ParsedSseEvent {
  payload: string
  type: string
}

interface StreamChatOptions {
  onContent: (chunk: string) => void
  onDone?: () => void
  onStatus?: (status: string) => void
  request: ChatbotChatRequest
  signal?: AbortSignal
}

// GET /chatbot/sessions
async function getChatbotSessions(signal?: AbortSignal): Promise<ChatbotSessionsResponse> {
  return await getRequest<ChatbotSessionsResponse>('/chatbot/sessions', { signal })
}

export function getChatbotSessionsQuery(useQueryOptions?: QueryOptions<ChatbotSessionsResponse>): UseQueryReturnType<ChatbotSessionsResponse, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_CHATBOT],
    queryFn: async ({ signal }) => await getChatbotSessions(signal),
    ...useQueryOptions,
  })
}

// GET /chatbot/sessions/{sessionId}/history
async function getChatbotSessionHistory(sessionId: string, signal?: AbortSignal): Promise<ChatbotSessionHistoryResponse> {
  return await getRequest<ChatbotSessionHistoryResponse>(`/chatbot/sessions/${sessionId}/history`, { signal })
}

export function getChatbotSessionHistoryQuery(sessionId: MaybeRef<string>, useQueryOptions?: QueryOptions<ChatbotSessionHistoryResponse>): UseQueryReturnType<ChatbotSessionHistoryResponse, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_CHATBOT_HISTORY, sessionId],
    queryFn: async ({ signal }) => await getChatbotSessionHistory(unref(sessionId), signal),
    ...useQueryOptions,
  })
}

// POST /chatbot/chat (SSE stream)
export async function streamChatbotResponse(options: StreamChatOptions) {
  const { onContent, onDone, onStatus, request, signal } = options
  const userStore = useUserStore()
  const headers = new Headers()

  headers.set(HEADER_API_VERSION, '2')
  headers.set(HEADER_CONTENT_TYPE, 'application/json')
  headers.set(HEADER_TIMEZONE, resolveTimezoneHeader())

  if (userStore.isLoggedIn()) {
    const userToken = await userStore.getUserToken()
    headers.set(HEADER_AUTHORIZATION, `Bearer ${userToken}`)
  }

  if (userStore.organisationId) {
    headers.set(HEADER_ORG_ID, userStore.organisationId)
  }

  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/chatbot/chat`, {
    body: JSON.stringify(request),
    headers,
    method: 'POST',
    signal,
  })

  if (!response.ok) {
    const errorMessage = await response.text()
    throw new Error(errorMessage || `Chatbot request failed with status ${response.status}`)
  }

  if (!response.body) {
    throw new Error('Chatbot stream response body is empty')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()

    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })

    const events = buffer.split('\n\n')
    buffer = events.pop() || ''

    for (const event of events) {
      const parsed = parseSseEvent(event)

      if (parsed === undefined) {
        continue
      }

      if (parsed.payload === CHATBOT_DONE_SENTINEL) {
        onDone?.()
        return
      }

      dispatchSseEvent(parsed, onContent, onStatus)
    }
  }

  const remainder = decoder.decode()

  if (remainder) {
    buffer += remainder
  }

  if (buffer) {
    const parsed = parseSseEvent(buffer)

    if (parsed !== undefined) {
      if (parsed.payload === CHATBOT_DONE_SENTINEL) {
        onDone?.()
        return
      }

      dispatchSseEvent(parsed, onContent, onStatus)
    }
  }

  onDone?.()
}

function dispatchSseEvent(event: ParsedSseEvent, onContent: (chunk: string) => void, onStatus?: (status: string) => void) {
  if (event.type === CHATBOT_SSE_EVENT_STATUS) {
    onStatus?.(event.payload)
    return
  }

  // Default and 'content' events both carry assistant reply text.
  onContent(event.payload)
}

function parseSseEvent(event: string): ParsedSseEvent | undefined {
  const lines = event.split('\n')
  const dataLines: string[] = []
  let type = CHATBOT_SSE_EVENT_CONTENT

  for (const rawLine of lines) {
    const line = rawLine.replace(CARRIAGE_RETURN_SUFFIX_REGEX, '')

    if (line.startsWith('event:')) {
      type = line.slice('event:'.length).trim() || CHATBOT_SSE_EVENT_CONTENT
      continue
    }

    if (!line.startsWith('data:')) {
      continue
    }

    const value = line.slice('data:'.length)

    // SSE spec: strip at most a single leading space after the colon.
    dataLines.push(value.startsWith(' ') ? value.slice(1) : value)
  }

  if (dataLines.length === 0) {
    return undefined
  }

  return {
    payload: dataLines.join('\n'),
    type,
  }
}
